import React, { useContext, useEffect, useReducer, useRef, useState, useCallback } from 'react';
import { UserContext } from '../../context/UserContext';
import CommonLayout from '../../components/CommonLayout';
import { fetchResusConfig, saveResusConfig } from './api/resusAdminApi';
import { reducer, makeInitialState, findDuplicateIds } from './state/configReducer';
import { buildWizardData, serializeWizardData, EMPTY_WIZARD_DATA } from './state/wizardData';
import StepProtocols from './steps/StepProtocols';
import StepMedications from './steps/StepMedications';
import StepArrangement from './steps/StepArrangement';
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';
import './ResusAdmin.scss';

interface HospitalLite {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
}

const APP_KEY = 'RESUS';

type Notification = { kind: 'success' | 'error' | 'info'; text: string } | null;

type ConfirmDialogState = {
  title: string;
  description: string;
  confirmText?: string;
  onConfirm: () => void;
} | null;

const ResusAdmin: React.FC = () => {
  const { user, hospitals, permittedApps } = useContext(UserContext) as any;

  const [editableHospitals, setEditableHospitals] = useState<HospitalLite[]>([]);
  const [availableHospitals, setAvailableHospitals] = useState<HospitalLite[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<Notification>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(null);

  // Initialize wizard state empty; real hydrate happens after we know the
  // hospital and fetch its raw configs. The reducer is created up-front so
  // child components can dispatch unconditionally.
  const [state, dispatch] = useReducer(
    reducer,
    null as any,
    () => makeInitialState(EMPTY_WIZARD_DATA)
  );

  // Track loaded hospital so re-renders don't re-fetch
  const loadedHospitalRef = useRef<string>('');

  // Permission scaffolding — same shape as TestTubes / PreemieNutrition, scoped to RESUS.
  // Super admins can edit every hospital plus the synthetic "apps" default; regular users
  // are limited to hospitals where they appear in `permittedApps[RESUS].admins`.
  useEffect(() => {
    if (!hospitals || !user || !permittedApps) return;

    let candidates: HospitalLite[] = [];
    if (user.isSuperAdmin) {
      candidates = [...hospitals];
    } else {
      const app = permittedApps.find((a: any) => a.key === APP_KEY);
      if (app) {
        const ids: string[] = app.admins
          .filter((admin: any) => admin.user._id === user._id)
          .map((admin: any) => admin.hospital._id);
        candidates = hospitals.filter((h: HospitalLite) => ids.includes(h._id));
      }
    }

    setEditableHospitals(candidates);

    const options: HospitalLite[] = user.isSuperAdmin
      ? [
          ...hospitals,
          { _id: 'apps', nameHebrew: 'ברירת מחדל', nameEnglish: 'apps' }
        ]
      : candidates;
    setAvailableHospitals(options);

    if (selectedHospital) return;
    if (user.isSuperAdmin) {
      setSelectedHospital('apps');
    } else if (candidates.length > 0) {
      setSelectedHospital(candidates[0].nameEnglish);
    }
  }, [hospitals, user, permittedApps, selectedHospital]);

  // Fetch the two raw configs (protocols + medications) for the selected
  // hospital and assemble the wizard shape. Both must complete before we
  // hydrate — medications reference protocol IDs, and the server falls back
  // to the "apps" hospital independently for each config.
  useEffect(() => {
    if (!selectedHospital || loadedHospitalRef.current === selectedHospital) return;
    loadedHospitalRef.current = selectedHospital;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const raw = await fetchResusConfig(selectedHospital);
        if (cancelled) return;
        const data = buildWizardData(raw.medicationsRaw, raw.protocolsRaw);
        dispatch({ type: 'HYDRATE', data });
      } catch (err) {
        console.error('Failed to load Resus config:', err);
        if (!cancelled) {
          dispatch({ type: 'HYDRATE', data: EMPTY_WIZARD_DATA });
          // Reset the ref so the user can re-trigger a load by reselecting
          // the same hospital after fixing the underlying error.
          loadedHospitalRef.current = '';
          setNotification({ kind: 'error', text: 'שגיאה בטעינת התצורה.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedHospital]);

  // Unsaved-changes guard for tab close. Step navigation does NOT prompt;
  // routing within the wizard preserves state via the reducer.
  useEffect(() => {
    if (!state.ui.dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.ui.dirty]);

  const handleSave = useCallback(async () => {
    if (!selectedHospital) return;

    const dups = findDuplicateIds(state.data);
    if (dups.length > 0) {
      setNotification({
        kind: 'error',
        text: `יש מזהים כפולים: ${dups.join(', ')}`
      });
      return;
    }

    setSaving(true);
    try {
      const raw = serializeWizardData(state.data);
      await saveResusConfig(selectedHospital, raw);
      dispatch({ type: 'MARK_CLEAN' });
      setNotification({ kind: 'success', text: 'התצורה נשמרה בהצלחה' });
    } catch (err: any) {
      console.error('Save failed:', err);
      setNotification({
        kind: 'error',
        text: err?.serverError?.hebMessage || 'שגיאה בשמירת התצורה'
      });
    } finally {
      setSaving(false);
    }
  }, [selectedHospital, state.data]);

  const handleHospitalChange = useCallback((nextHospital: string) => {
    if (nextHospital === selectedHospital) return;
    if (state.ui.dirty) {
      setConfirmDialog({
        title: 'יש שינויים שלא נשמרו',
        description: 'להחליף בית חולים בכל זאת?',
        confirmText: 'החלף',
        onConfirm: () => {
          setConfirmDialog(null);
          setSelectedHospital(nextHospital);
        }
      });
      return;
    }
    setSelectedHospital(nextHospital);
  }, [selectedHospital, state.ui.dirty]);

  // Auto-dismiss notifications
  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(t);
  }, [notification]);

  if (!user) {
    return (
      <CommonLayout>
        <div className="resus-admin">
          <p>טוען מידע משתמש...</p>
        </div>
      </CommonLayout>
    );
  }

  if (!user.isSuperAdmin && editableHospitals.length === 0) {
    return (
      <CommonLayout>
        <div className="resus-admin">
          <h1 className="resus-admin__title">ניהול חדר הלם</h1>
          <p className="resus-admin__subtle">
            אין לך הרשאות עריכה לאף בית חולים עבור אפליקציית חדר ההלם. פנה למנהל המערכת.
          </p>
        </div>
      </CommonLayout>
    );
  }

  const showHospitalDropdown = user.isSuperAdmin || editableHospitals.length > 1;
  const dropdownOptions: HospitalLite[] = availableHospitals;

  return (
    <CommonLayout maxWidth="1300px">
      <div className="resus-admin">
        <header className="resus-admin__header">
          <h1 className="resus-admin__title">ניהול חדר הלם</h1>

          {showHospitalDropdown && dropdownOptions.length > 0 && (
            <div className="resus-admin__hospital">
              <label htmlFor="resus-admin-hospital">בית חולים:</label>
              <select
                id="resus-admin-hospital"
                value={selectedHospital}
                onChange={(e) => handleHospitalChange(e.target.value)}
              >
                {dropdownOptions.map(h => (
                  <option key={h._id} value={h.nameEnglish}>{h.nameHebrew}</option>
                ))}
              </select>
            </div>
          )}
        </header>

        <nav className="resus-admin__steps" aria-label="שלבי האשף">
          {[1, 2, 3].map((step) => {
            const labels = { 1: 'פרוטוקולים', 2: 'תרופות', 3: 'התאמה לפרוטוקול' };
            const isActive = state.ui.currentStep === step;
            return (
              <button
                key={step}
                type="button"
                className={`resus-admin__step-btn${isActive ? ' resus-admin__step-btn--active' : ''}`}
                onClick={() => dispatch({ type: 'SET_STEP', step: step as 1 | 2 | 3 })}
              >
                <span className="resus-admin__step-btn__index">{step}</span>
                {labels[step as 1 | 2 | 3]}
              </button>
            );
          })}
        </nav>

        {loading ? (
          <p className="resus-admin__subtle">טוען נתונים...</p>
        ) : (
          <>
            {state.ui.currentStep === 1 && (
              <StepProtocols state={state} dispatch={dispatch} hospital={selectedHospital} setNotification={setNotification} />
            )}
            {state.ui.currentStep === 2 && (
              <StepMedications state={state} dispatch={dispatch} hospital={selectedHospital} />
            )}
            {state.ui.currentStep === 3 && (
              <StepArrangement state={state} dispatch={dispatch} />
            )}

            <div className="resus-admin__sticky-actions">
              <div>
                {state.ui.dirty && <span className="resus-admin__dirty-badge">● יש שינויים שלא נשמרו</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="resus-admin__btn resus-admin__btn--primary"
                  onClick={handleSave}
                  disabled={saving || !state.ui.dirty}
                >
                  {saving ? 'שומר...' : 'שמירה'}
                </button>
              </div>
            </div>
          </>
        )}

        {notification && (
          <div className={`resus-admin__notification resus-admin__notification--${notification.kind}`}>
            {notification.text}
          </div>
        )}

        <ConfirmationDialog
          open={!!confirmDialog}
          onClose={() => setConfirmDialog(null)}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={() => confirmDialog?.onConfirm()}
          title={confirmDialog?.title ?? ''}
          description={confirmDialog?.description ?? ''}
          confirmText={confirmDialog?.confirmText ?? 'אישור'}
          cancelText="ביטול"
        >
          {null}
        </ConfirmationDialog>
      </div>
    </CommonLayout>
  );
};

export default ResusAdmin;
